import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserPlus, Check, X, Clock } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useState } from "react";

export function PartnerMatching() {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [requestMessage, setRequestMessage] = useState("");

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  // Get potential partners (users in same challenges)
  const { data: potentialPartners } = useQuery({
    queryKey: ['potential-partners', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get users from challenges I'm in
      const { data: myChallenges } = await supabase
        .from('challenge_participants')
        .select('challenge_id')
        .eq('user_id', user.id)
        .eq('completion_status', 'active');

      if (!myChallenges || myChallenges.length === 0) return [];

      const challengeIds = myChallenges.map(c => c.challenge_id);

      const { data, error } = await supabase
        .from('challenge_participants')
        .select('user_id')
        .in('challenge_id', challengeIds)
        .neq('user_id', user.id)
        .eq('completion_status', 'active');

      if (error) throw error;

      // Get unique user IDs
      const userIds = [...new Set(data.map(p => p.user_id))];

      // Check existing partnerships
      const { data: existingPartners } = await supabase
        .from('accountability_partnerships')
        .select('partner_id, user_id')
        .or(`user_id.eq.${user.id},partner_id.eq.${user.id}`)
        .in('status', ['pending', 'active']);

      const existingIds = new Set(
        existingPartners?.map(p => 
          p.user_id === user.id ? p.partner_id : p.user_id
        ) || []
      );

      // Filter out existing partners
      const availableIds = userIds.filter(id => !existingIds.has(id));

      // Get profiles
      if (availableIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', availableIds);

      return profiles || [];
    },
    enabled: !!user?.id,
  });

  // Get pending requests
  const { data: pendingRequests } = useQuery({
    queryKey: ['pending-requests', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('partnership_requests')
        .select('*')
        .eq('requestee_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get requester profiles
      const userIds = data.map(r => r.requester_id);
      if (userIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      return data.map(r => ({
        ...r,
        requester_profile: profileMap.get(r.requester_id),
      }));
    },
    enabled: !!user?.id,
  });

  const sendRequestMutation = useMutation({
    mutationFn: async ({ userId, message }: { userId: string; message: string }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('partnership_requests')
        .insert({
          requester_id: user.id,
          requestee_id: userId,
          message,
        });

      if (error) throw error;

      // Create notification
      await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type: 'partnership_request',
          title: 'New Partnership Request',
          message: `Someone wants to be your accountability partner!`,
          related_user_id: user.id,
          link: '/connect-and-grow',
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['potential-partners'] });
      toast.success("Partnership request sent!");
      setSelectedUser(null);
      setRequestMessage("");
    },
  });

  const respondToRequestMutation = useMutation({
    mutationFn: async ({ requestId, accept }: { requestId: string; accept: boolean }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { data: request, error: fetchError } = await supabase
        .from('partnership_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (fetchError) throw fetchError;

      // Update request status
      const { error } = await supabase
        .from('partnership_requests')
        .update({
          status: accept ? 'accepted' : 'rejected',
          responded_at: new Date().toISOString(),
        })
        .eq('id', requestId);

      if (error) throw error;

      if (accept) {
        // Create partnership
        await supabase
          .from('accountability_partnerships')
          .insert({
            user_id: request.requester_id,
            partner_id: user.id,
            status: 'active',
            accepted_at: new Date().toISOString(),
          });

        // Notify requester
        await supabase
          .from('notifications')
          .insert({
            user_id: request.requester_id,
            type: 'partnership_accepted',
            title: 'Partnership Accepted!',
            message: `Your partnership request was accepted!`,
            link: '/connect-and-grow',
          });
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pending-requests'] });
      queryClient.invalidateQueries({ queryKey: ['partnerships'] });
      toast.success(variables.accept ? "Partnership accepted!" : "Request declined");
    },
  });

  return (
    <div className="space-y-6">
      {/* Pending Requests */}
      {pendingRequests && pendingRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Pending Requests ({pendingRequests.length})
            </CardTitle>
            <CardDescription>Partnership requests waiting for your response</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingRequests.map((request) => (
              <div key={request.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3 flex-1">
                  <Avatar>
                    <AvatarFallback>
                      {request.requester_profile?.display_name?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium">{request.requester_profile?.display_name || 'User'}</p>
                    {request.message && (
                      <p className="text-sm text-muted-foreground">{request.message}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => respondToRequestMutation.mutate({ requestId: request.id, accept: true })}
                    disabled={respondToRequestMutation.isPending}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => respondToRequestMutation.mutate({ requestId: request.id, accept: false })}
                    disabled={respondToRequestMutation.isPending}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Find Partners */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-secondary" />
            Find Accountability Partners
          </CardTitle>
          <CardDescription>Connect with others in your challenges</CardDescription>
        </CardHeader>
        <CardContent>
          {potentialPartners && potentialPartners.length > 0 ? (
            <div className="space-y-3">
              {potentialPartners.map((partner) => (
                <div key={partner.user_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback>{partner.display_name?.[0] || 'U'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{partner.display_name || 'User'}</p>
                      <p className="text-xs text-muted-foreground">In your challenges</p>
                    </div>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm" onClick={() => setSelectedUser(partner.user_id)}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Request
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Send Partnership Request</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback>{partner.display_name?.[0] || 'U'}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{partner.display_name || 'User'}</p>
                            <p className="text-sm text-muted-foreground">Accountability Partner</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Message (optional)</label>
                          <Textarea
                            placeholder="Hi! I'd love to be accountability partners..."
                            value={requestMessage}
                            onChange={(e) => setRequestMessage(e.target.value)}
                            rows={3}
                          />
                        </div>
                        <Button
                          className="w-full"
                          onClick={() => {
                            if (selectedUser) {
                              sendRequestMutation.mutate({
                                userId: selectedUser,
                                message: requestMessage,
                              });
                            }
                          }}
                          disabled={sendRequestMutation.isPending}
                        >
                          Send Request
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <UserPlus className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                Join challenges to find potential accountability partners!
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
